import React from 'react';
import SecuritySection from './ui/SecuritySection';
import SecuritySeverityBadge from './ui/SecuritySeverityBadge';
import SecurityEmptyState from './ui/SecurityEmptyState';
import { IconShield } from '../../ui/adminIcons';

function PatternStat({ label, value, tone }) {
  return (
    <div className="vx-sec-kv vx-sec-kv--block">
      <span className="vx-sec-kv-label">{label}</span>
      <span className={`vx-sec-kv-value vx-sec-kv-value--${tone}`}>{value}</span>
    </div>
  );
}

function EffectivenessBar({ label, percent }) {
  return (
    <div className="vx-sec-effectiveness">
      <span className="vx-sec-effectiveness-label">{label}</span>
      <div className="vx-sec-effectiveness-track">
        <div className="vx-sec-effectiveness-fill" style={{ width: `${percent}%` }} />
      </div>
      <span className="vx-sec-effectiveness-value">{percent}%</span>
    </div>
  );
}

export default function SecurityThreatAnalysisPanel({ dashboardData, protectionStats }) {
  const { threatAnalysis, recommendations = [] } = dashboardData;
  if (!threatAnalysis) return null;

  const threatIntelPercent = Math.max(90 - protectionStats.riskScoring.riskScore * 5, 75);

  return (
    <SecuritySection title="Threat analysis" icon={<IconShield />} id="security-threat-analysis">
      <div className="vx-sec-grid vx-sec-grid--analysis">
        <div className="vx-sec-panel">
          <h3 className="vx-sec-panel-title">Attack patterns</h3>
          <PatternStat
            label="File access attempts"
            value={threatAnalysis.fileAccessAttempts}
            tone={threatAnalysis.fileAccessAttempts > 0 ? 'critical' : 'safe'}
          />
          <PatternStat
            label="Malicious bots"
            value={threatAnalysis.maliciousBots}
            tone={threatAnalysis.maliciousBots > 0 ? 'warning' : 'safe'}
          />
          <PatternStat
            label="High-risk IPs"
            value={threatAnalysis.highRiskIPs}
            tone={threatAnalysis.highRiskIPs > 5 ? 'warning' : 'safe'}
          />
        </div>

        <div className="vx-sec-panel">
          <h3 className="vx-sec-panel-title">Recommendations</h3>
          {recommendations.length > 0 ? (
            <ul className="vx-sec-rec-list">
              {recommendations.map((rec, index) => (
                <li key={index} className={`vx-sec-rec-item vx-sec-rec-item--${rec.priority.toLowerCase()}`}>
                  <div className="vx-sec-rec-header">
                    <SecuritySeverityBadge severity={rec.priority} />
                    <span className="vx-sec-rec-action">{rec.action}</span>
                  </div>
                  <p className="vx-sec-rec-desc">{rec.description}</p>
                  <p className="vx-sec-rec-impl">{rec.implementation}</p>
                </li>
              ))}
            </ul>
          ) : (
            <SecurityEmptyState title="All measures operating optimally" lead="No immediate action required." />
          )}
        </div>

        <div className="vx-sec-panel">
          <h3 className="vx-sec-panel-title">Protection effectiveness</h3>
          <EffectivenessBar label="File protection" percent={protectionStats.fileProtection.effectiveness} />
          <EffectivenessBar label="Bot protection" percent={protectionStats.botProtection.effectiveness} />
          <EffectivenessBar label="Threat intelligence" percent={threatIntelPercent} />
        </div>
      </div>
    </SecuritySection>
  );
}
